import { use_event_listener_default } from './chunk-3HBUA6YI.mjs';
import { use_mount_default } from './chunk-XS6BHIMP.mjs';
import { use_update_effect_default } from './chunk-WUKIS7AT.mjs';
import { is_browser_default } from './chunk-DOLOJKHH.mjs';
import { isString, isFunction, use_memoized_fn_default, isUndef } from './chunk-24OWA2BQ.mjs';
import Cookies from 'js-cookie';
import { useState, useRef } from 'react';

var useCookieState = (cookieKey, options = {}) => {
  const [state, setState] = useState(() => {
    const cookieValue = Cookies.get(cookieKey);
    if (isString(cookieValue)) {
      return cookieValue;
    }
    if (isFunction(options.defaultValue)) {
      return options.defaultValue();
    }
    return options.defaultValue;
  });
  const updateState = use_memoized_fn_default(
    (newValue, newOptions = {}) => {
      const { defaultValue, ...restOptions } = { ...options, ...newOptions };
      const value = isFunction(newValue) ? newValue(state) : newValue;
      setState(value);
      if (value === void 0) {
        Cookies.remove(cookieKey);
      } else {
        Cookies.set(cookieKey, value, restOptions);
      }
    }
  );
  return [state, updateState];
};
var use_cookie_state_default = useCookieState;
var SYNC_STORAGE_EVENT_NAME = "AHOOKS_SYNC_STORAGE_EVENT_NAME";
var createUseStorageState = (
  /**
   * Returns the storage-backed state hook factory for the storage provided by getStorage.
   *
   * @param getStorage - Returns the storage to use, or undefined when none is available.
   * @returns A state hook that persists its value in that storage.
   */
  (getStorage) => (
    /**
     * Creates a state hook that persists its value under key in the storage returned by getStorage.
     *
     * @param key - The storage key to read and write.
     * @param options - Default value, serializers, cross-tab sync and error handling.
     * @returns The current value and a setter that also writes to storage.
     */
    (key, options = {}) => {
      let storage;
      const { listenStorageChange = false, getInitialValueInEffect = false } = options;
      const serializer = isFunction(options.serializer) ? options.serializer : (value) => JSON.stringify(value);
      const deserializer = isFunction(
        options.deserializer
      ) ? options.deserializer : (value) => JSON.parse(value);
      const onError = isFunction(options.onError) ? options.onError : console.error;
      try {
        storage = getStorage();
      } catch (error) {
        onError(error);
      }
      const getStoredValue = () => {
        try {
          const raw = storage?.getItem(key);
          if (raw) {
            return deserializer(raw);
          }
        } catch (error) {
          onError(error);
        }
        return getDefaultValue();
      };
      const getDefaultValue = () => {
        if (isFunction(options.defaultValue)) {
          return options.defaultValue();
        }
        return options.defaultValue;
      };
      const [state, setState] = useState(() => {
        if (getInitialValueInEffect) {
          return getDefaultValue();
        }
        return getStoredValue();
      });
      use_mount_default(() => {
        if (!getInitialValueInEffect) {
          return;
        }
        setState(getStoredValue());
      });
      const stateRef = useRef(state);
      stateRef.current = state;
      use_update_effect_default(() => {
        const nextState = getStoredValue();
        if (Object.is(nextState, stateRef.current)) {
          return;
        }
        stateRef.current = nextState;
        setState(nextState);
      }, [key]);
      const updateState = (value) => {
        const previousState = stateRef.current;
        const currentState = isFunction(value) ? value(previousState) : value;
        if (Object.is(currentState, previousState)) {
          return;
        }
        if (!listenStorageChange) {
          stateRef.current = currentState;
          setState(currentState);
        }
        try {
          let newValue;
          const oldValue = storage?.getItem(key);
          if (isUndef(currentState)) {
            newValue = null;
            storage?.removeItem(key);
          } else {
            newValue = serializer(currentState);
            storage?.setItem(key, newValue);
          }
          dispatchEvent(
            // send custom event to communicate within same page
            // importantly this should not be a StorageEvent since those cannot
            // be constructed with a non-built-in storage area
            new CustomEvent(SYNC_STORAGE_EVENT_NAME, {
              detail: {
                key,
                newValue,
                oldValue,
                storageArea: storage
              }
            })
          );
        } catch (error) {
          onError(error);
        }
      };
      const syncState = (event) => {
        if (event.key !== key || event.storageArea !== storage) {
          return;
        }
        const nextState = getStoredValue();
        if (Object.is(nextState, stateRef.current)) {
          return;
        }
        stateRef.current = nextState;
        setState(nextState);
      };
      const syncStateFromCustomEvent = (event) => {
        syncState(event.detail);
      };
      use_event_listener_default("storage", syncState, {
        enable: listenStorageChange
      });
      use_event_listener_default(SYNC_STORAGE_EVENT_NAME, syncStateFromCustomEvent, {
        enable: listenStorageChange
      });
      return [state, use_memoized_fn_default(updateState)];
    }
  )
);

// src/storage/use-local-storage-state.ts
var useLocalStorageState = createUseStorageState(
  () => is_browser_default ? localStorage : void 0
);
var use_local_storage_state_default = useLocalStorageState;

// src/storage/use-session-storage-state.ts
var useSessionStorageState = createUseStorageState(
  () => is_browser_default ? sessionStorage : void 0
);
var use_session_storage_state_default = useSessionStorageState;

export { use_cookie_state_default, use_local_storage_state_default, use_session_storage_state_default };
